import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FitMaskLookup } from 'src/app/compliance/shared/fit-mask/fit-mask-lookup';
import { ShippingStatusUpdate } from 'src/app/compliance/shipping-requests/models';
import { ShippingRequest } from 'src/app/compliance/shipping-requests/shipping-request';
import { ShippingRequestGridItem } from 'src/app/compliance/shipping-requests/shipping-request-grid-item';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { UploadAttachment } from 'src/app/shared/components/upload-attachments/upload-attachment.model';
import { PagingToken, PagingTokenFixed } from 'src/app/shared/models/core';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { KeyValuePair } from 'src/app/shared/models/key-value-pair.model';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { appendQuery, combine } from 'src/app/shared/utilities/route';
import { FetchArgs } from './../../shared/models/core';
import { ShippingRequestCandidate } from './models';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class ShippingRequestsService {
    private readonly coreApiNovaPath: string;
    private readonly shippingRequestsUrl: string;

    public featureFlag = FeatureFlag;

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        @Inject(APP_CONFIG) private readonly settings: Settings,
        private readonly httpClient: HttpClient
    ) {
        this.coreApiNovaPath = combine(this.settings.coreAPIUrl, 'ayanova');
        this.shippingRequestsUrl = combine(this.coreApiNovaPath, '/compliance/shipping-requests');
    }

    // ** ShippingRequestsController.Search
    searchShippingRequests(args: any): Observable<PagingToken<ShippingRequestGridItem[]>> {
        const url = `${this.shippingRequestsUrl}/search`;
        const result = this.getPaged<ShippingRequestGridItem>(args.pageSize, args.skip, args.filter, args.sort, url);
        return result.pipe(map((pipedResult) => this.mapPackageContents(pipedResult)));
    }

    mapPackageContents(result: PagingToken<ShippingRequestGridItem[]>): PagingToken<ShippingRequestGridItem[]> {
        if (result.data && result.data.length) {
            result.data.forEach((item) => {
                const contents = [];

                if (item.packageContents?.length) {
                    item.packageContents.forEach((a) => contents.push(`${a.quantity} x ${a.brand} ${a.name}`));
                }

                if (item.otherFitMask) {
                    contents.push(`${item.otherFitMaskQuantity || '1'} x ${item.otherFitMask}`);
                }

                if (item.miscPackageContents?.length) {
                    item.miscPackageContents.forEach((a) => contents.push(`${a.quantity} x ${a.name}`));
                }

                if (item.miscellaneousOther) {
                    contents.push(`1 x ${item.miscellaneousOther}`);
                }

                item.packageContentMapped = contents.join('<br/>').trim();
            });
        }
        return result;
    }

    // ** ShippingRequestsController.BulkStatusUpdate
    updateShippingStatus(shippingRequest: ShippingStatusUpdate): Observable<any> {
        const url = `${this.shippingRequestsUrl}/bulk-status-update`;
        const result = this.httpClient.post<ShippingRequest>(url, shippingRequest);
        return result;
    }

    // ** ShippingRequestsController.GetDetails
    getShippingRequest(id: number): Observable<ShippingRequest> {
        const url = `${this.shippingRequestsUrl}/${id}`;
        const result = this.httpClient.get<ShippingRequest>(url).pipe(
            map((shippingRequest) => {
                return shippingRequest;
            })
        );
        return result;
    }

    // ** ShippingRequestsController.Add
    addShippingRequest(shippingRequest: ShippingRequest): Observable<ShippingRequest> {
        const url = `${this.shippingRequestsUrl}`;
        const result = this.httpClient.post<ShippingRequest>(url, shippingRequest);
        return result;
    }

    // ** ShippingRequestsController.Update
    updateShippingRequest(id: number, shippingRequest: ShippingRequest): Observable<ShippingRequest> {
        const url = `${this.shippingRequestsUrl}/${id}`;
        const result = this.httpClient.put<ShippingRequest>(url, shippingRequest);
        return result;
    }

    // ** LookupController.GetPacketRequestFitMaskLookups
    getPacketRequestFitMasksLookups(): Observable<FitMaskLookup[]> {
        return this.httpClient.get<FitMaskLookup[]>(`${this.coreApiNovaPath}/lookup/packet-request-fit-masks`);
    }

    // ** LookupController.GetPacketRequestMiscellaneousItemsLookups
    getPacketRequestMiscellaneousItemsLookups(): Observable<Lookup[]> {
        return this.httpClient.get<Lookup[]>(`${this.coreApiNovaPath}/lookup/packet-request-miscellaneous-items`);
    }

    // ** ShippingRequestsController.SearchCandidates
    getCandidates(searchText: string): Observable<ShippingRequestCandidate[]> {
        const url = `${this.coreApiNovaPath}/compliance/shipping-requests/candidates?name=${searchText}`;

        const result = this.httpClient.get<ShippingRequestCandidate[]>(url);

        return result;
    }

    // ** ShippingRequestsController.GetAssignees
    getAssignees(): Observable<Lookup[]> {
        const url = `${this.coreApiNovaPath}/compliance/shipping-requests/assignees`;

        const result = this.httpClient.get<Lookup[]>(url);
        return result;
    }

    // ** ShippingRequestsController.GetDocuments
    getShippingRequestAttachments(shippingId, args: FetchArgs): Observable<PagingTokenFixed<UploadAttachment[]>> {
        const url = `${this.shippingRequestsUrl}/${shippingId}/documents`;
        const result = this.getPagedMethodGet<UploadAttachment>(args.pageSize, args.skip, url);
        return result;
    }

    // ** ShippingRequestsController.UploadDocuments
    uploadShippingRequestAttachments(shippingId: number, formData: FormData): Observable<unknown> {
        const url = `${this.shippingRequestsUrl}/${shippingId}/documents`;
        const result = this.httpClient.put<unknown>(url, formData);
        return result;
    }

    // ** ShippingRequestsController.RemoveDocument
    removeShippingRequestAttachments(shippingId: number, documentId: number): Observable<unknown> {
        const url = `${this.shippingRequestsUrl}/${shippingId}/documents/${documentId}`;
        const result = this.httpClient.delete<unknown>(url);
        return result;
    }

    // ** ShippingRequestsController.DownloadFile
    getShippingRequestFile(shippingId: number, documentId: number): Observable<{ file: Blob; url: string }> {
        return this.httpClient
            .get(`${this.shippingRequestsUrl}/${shippingId}/documents/${documentId}`, { responseType: 'blob' })
            .pipe(
                map((file) => {
                    const url = this.window.window.URL.createObjectURL(file);
                    return { file, url };
                })
            );
    }

    // ** ShippingRequestsController.ReassignTickets
    reassignBulkTickets(assigneeId: number, ticketIds: number[]): Observable<unknown> {
        const url = `${this.shippingRequestsUrl}/reassign/${assigneeId}`;
        return this.httpClient.put<unknown>(url, ticketIds);
    }

    // ** ShippingRequestsController.GetChainOfCustodyLookupAsync
    getChainOfCustodyLookups(): Observable<Array<KeyValuePair<number, string>>> {
        const url = `${this.shippingRequestsUrl}/chainOfCustodyLookups`;
        return this.httpClient.get<Array<KeyValuePair<number, string>>>(url);
    }

    protected getPagedMethodGet<T>(pageSize: number, skip: number, url: string): Observable<PagingTokenFixed<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.httpClient.get<PagingTokenFixed<T[]>>(path);
    }

    protected getPaged<T>(
        pageSize: number,
        skip: number,
        filter: object,
        sort: object,
        url: string
    ): Observable<PagingToken<T[]>> {
        const body = { pageSize, skip, filter, sort };
        const result = this.httpClient.post<PagingToken<T[]>>(url, body).pipe(map((resp) => resp));
        return result;
    }
}
